import { useState } from 'react';
import { Mail, CheckCircle2, RefreshCw, Loader2, LogOut } from 'lucide-react';
import { useAuthStore } from '@/stores/auth-store';
import { sendVerificationEmail, reloadUser } from '@/lib/firebase/auth';

export function EmailVerificationPrompt() {
  const user = useAuthStore((s) => s.user);
  const [verificationSent, setVerificationSent] = useState(false);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSendVerification = async () => {
    try {
      await sendVerificationEmail();
      setVerificationSent(true);
      setError(null);
    } catch {
      setError('Failed to send verification email. Please try again.');
    }
  };

  const handleVerified = async () => {
    setChecking(true);
    setError(null);
    try {
      const freshUser = await reloadUser();
      if (freshUser.emailVerified) {
        useAuthStore.setState({ needsEmailVerification: false });
        window.location.reload();
      } else {
        setError('Email not yet verified. Please check your inbox and try again.');
      }
    } catch {
      setError('Failed to check verification status.');
    }
    setChecking(false);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center space-y-2">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <Mail className="h-6 w-6 text-primary" />
          </div>
          <h1 className="text-2xl font-bold text-foreground">Verify your email</h1>
          <p className="text-sm text-muted-foreground">
            Please verify your email address <strong>{user?.email}</strong>. Check your inbox including spam & notifications folder.
          </p>
        </div>

        <div className="space-y-3">
          <button
            onClick={handleSendVerification}
            disabled={verificationSent}
            className="w-full rounded-lg border border-border px-4 py-2.5 text-sm font-medium text-foreground hover:bg-muted disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
          >
            {verificationSent ? (
              <>
                <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                Verification email sent
              </>
            ) : (
              <>
                <Mail className="h-4 w-4" />
                Send verification email
              </>
            )}
          </button>

          <button
            onClick={handleVerified}
            disabled={checking}
            className="w-full rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
          >
            {checking ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            I have verified
          </button>
        </div>

        {error && (
          <div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </div>
        )}

        <button
          onClick={() => useAuthStore.getState().signOut()}
          className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center justify-center gap-1.5"
        >
          <LogOut className="h-3.5 w-3.5" />
          Sign out
        </button>

        <p className="text-center text-xs text-muted-foreground">
          If you did not receive any mail,{' '}
          <a href="https://apps.ulgebra.com/contact" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary/80 font-medium transition-colors">
            Contact Us
          </a>
        </p>
      </div>
    </div>
  );
}
